import { PServed } from '@wui/im'

import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'


import { useLocalization } from '@/shared/composables/localization'

import { isGuestIdentity } from './is-guest-identity'

export const formatGuestDisplayName = (name: string) => {
  const { t } = useLocalization()
  return `${name} (${t('common.guest')})`
}

const findGuestNameInRoom = (roomPServed: PServed, guestPServed: PServed): string | undefined => {
  const { findGuestByPserved, findByPServed } = useStoreRooms()

  const byKey = findGuestByPserved(roomPServed, guestPServed)
  if (byKey) {
    return byKey
  }

  const roomGuests = findByPServed(roomPServed)?.guests
  if (!roomGuests?.size) {
    return undefined
  }

  for (const [key, guest] of roomGuests) {
    if ((key === guestPServed || guest.pServed === guestPServed) && guest.name) {
      return guest.name
    }
  }

  return undefined
}

type ResolveGuestDisplayNameOptions = {
  fallbackToGenericGuest?: boolean
  /** Current authenticated guest (avoid importing useGuestAuth in shared util) */
  selfGuest?: { pServed: string, name: string }
}

/**
 * Resolves display name for a guest in a room.
 * Multi-guest rooms require an exact guestPServed match (Map key or guest.pServed).
 * Single-guest room may fall back to the only participant when lookup misses.
 */
export const resolveGuestDisplayName = (
  roomPServed: PServed,
  guestPServed: PServed | undefined,
  options?: ResolveGuestDisplayNameOptions,
): string | undefined => {
  if (!isGuestIdentity(guestPServed) || !guestPServed) {
    return
  }

  const { findGuestById } = useStoreContacts()
  const { t } = useLocalization()
  const fallbackToGenericGuest = options?.fallbackToGenericGuest ?? true
  const selfGuest = options?.selfGuest

  const guestName = findGuestNameInRoom(roomPServed, guestPServed)
    ?? findGuestById(guestPServed)?.name
    ?? (selfGuest?.pServed === guestPServed ? selfGuest.name : undefined)

  if (guestName) {
    return formatGuestDisplayName(guestName)
  }

  const roomGuests = useStoreRooms().findByPServed(roomPServed)?.guests
  if (roomGuests?.size === 1) {
    const [onlyGuest] = roomGuests.values()
    if (onlyGuest?.name) {
      return formatGuestDisplayName(onlyGuest.name)
    }
  }

  return fallbackToGenericGuest ? t('common.guest') : undefined
}
